import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardBody, Button, Input, Textarea, Icon } from '@repo/ui';
import { PageLayout } from '@xtnd/web/shared/layout';
import { authApi } from '@xtnd/web/features/auth/api/authApi';
import { useSubmitFeedback } from '../../queries/useFeedbackQuery';
import type { FeedbackCategory, CreateFeedbackRequest, PageContext } from '../../types';

interface CategoryOption {
  value: FeedbackCategory;
  label: string;
  description: string;
  icon: string;
  color: string;
  bgColor: string;
  borderColor: string;
  textColor: string;
}

const categoryOptions: CategoryOption[] = [
  {
    value: 'general',
    label: 'General Feedback',
    description: 'Share your thoughts and suggestions',
    icon: 'HiChat',
    color: '#76C5BB',
    bgColor: 'bg-blue-100 dark:bg-blue-900/20',
    borderColor: 'border-blue-300 dark:border-blue-700',
    textColor: 'text-blue-600 dark:text-blue-400',
  },
  {
    value: 'bug',
    label: 'Bug Report',
    description: 'Report issues or unexpected behavior',
    icon: 'FaBug',
    color: '#EF4444',
    bgColor: 'bg-red-100 dark:bg-red-900/20',
    borderColor: 'border-red-300 dark:border-red-700',
    textColor: 'text-red-600 dark:text-red-400',
  },
  {
    value: 'feature',
    label: 'Feature Request',
    description: 'Suggest new functionality or improvements',
    icon: 'HiLightBulb',
    color: '#F59E0B',
    bgColor: 'bg-amber-100 dark:bg-amber-900/20',
    borderColor: 'border-amber-300 dark:border-amber-700',
    textColor: 'text-amber-600 dark:text-amber-400',
  },
];

export function FeedbackPage() {
  const currentUser = authApi.getCurrentUser();
  const submitFeedback = useSubmitFeedback();

  const [category, setCategory] = useState<FeedbackCategory | null>(null);
  const [message, setMessage] = useState('');
  const [userEmail, setUserEmail] = useState(currentUser?.email || '');
  const [errors, setErrors] = useState<{
    category?: string;
    message?: string;
    userEmail?: string;
  }>({});
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Capture page context on mount
  useEffect(() => {
    // Page context will be captured when submitting
  }, []);

  // Reset form after successful submission
  useEffect(() => {
    if (submitFeedback.isSuccess && isSubmitted) {
      // Reset form after a delay to show success message
      setTimeout(() => {
        setCategory(null);
        setMessage('');
        setIsSubmitted(false);
      }, 5000);
    }
  }, [submitFeedback.isSuccess, isSubmitted]);

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    if (!category) {
      newErrors.category = 'Please select a feedback category';
    }

    if (!message.trim()) {
      newErrors.message = 'Please enter your feedback message';
    } else if (message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    } else if (message.length > 5000) {
      newErrors.message = 'Message must be less than 5000 characters';
    }

    if (!userEmail.trim()) {
      newErrors.userEmail = 'Please enter your email address';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userEmail)) {
      newErrors.userEmail = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Capture page context
    const pageContext: PageContext = {
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      viewportSize: `${window.innerWidth}x${window.innerHeight}`,
    };

    const feedbackData: CreateFeedbackRequest = {
      category: category!,
      message: message.trim(),
      userEmail: userEmail.trim(),
      pageContext,
    };

    setIsSubmitted(true);
    await submitFeedback.mutateAsync(feedbackData);
  };

  const handleCategorySelect = (selectedCategory: FeedbackCategory) => {
    setCategory(selectedCategory);
    if (errors.category) {
      setErrors(prev => ({ ...prev, category: undefined }));
    }
  };

  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    if (errors.message) {
      setErrors(prev => ({ ...prev, message: undefined }));
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserEmail(e.target.value);
    if (errors.userEmail) {
      setErrors(prev => ({ ...prev, userEmail: undefined }));
    }
  };

  return (
    <PageLayout
      title="Feedback"
      description="Share your thoughts, report bugs, or suggest new features. We value your input!"
      icon="HiChat"
    >
      {submitFeedback.isSuccess && isSubmitted ? (
        <Card>
          <CardBody>
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-xtnd-blue/10 dark:bg-xtnd-blue/20 mb-4">
                <Icon name="HiCheckCircle" size="xl" className="text-xtnd-blue" />
              </div>
              <h2 className="text-2xl font-bold text-xtnd-dark dark:text-xtnd-white mb-2">
                Thank You!
              </h2>
              <p className="text-base text-xtnd-dark-600 dark:text-xtnd-light mb-4 max-w-md mx-auto">
                Your feedback has been submitted successfully. We appreciate your input and will
                review it shortly.
              </p>
              <div className="inline-flex items-center gap-2 text-sm text-xtnd-dark-500 dark:text-xtnd-light">
                <Icon name="HiClock" size="sm" />
                <span>You can submit another feedback in a moment</span>
              </div>
            </div>
          </CardBody>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <h2 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-white">
              Submit Feedback
            </h2>
            <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-light mt-1">
              Help us improve by sharing your feedback, reporting issues, or suggesting new
              features.
            </p>
          </CardHeader>
          <CardBody>
            {/* Tips for effective feedback */}
            <div className="mb-6 p-4 rounded-lg bg-xtnd-light/30 dark:bg-xtnd-dark-800/50 border border-xtnd-light dark:border-xtnd-dark-700">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 rounded-lg bg-xtnd-blue/10 dark:bg-xtnd-blue/20 flex items-center justify-center">
                    <Icon name="HiInformationCircle" size="sm" className="text-xtnd-blue" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-xtnd-dark dark:text-xtnd-white mb-1">
                    Tips for effective feedback
                  </h3>
                  <ul className="text-xs text-xtnd-dark-600 dark:text-xtnd-light space-y-0.5 list-disc list-inside">
                    <li>Be specific and provide context</li>
                    <li>Include steps to reproduce for bug reports</li>
                    <li>Explain the benefit for feature requests</li>
                  </ul>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Category Selection */}
              <div>
                <label className="block text-sm font-medium text-xtnd-dark dark:text-xtnd-white mb-3">
                  What type of feedback are you sharing? <span className="text-red-500">*</span>
                </label>
                {errors.category && (
                  <p className="text-sm text-red-600 dark:text-red-400 mb-2">{errors.category}</p>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {categoryOptions.map(option => {
                    const isSelected = category === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handleCategorySelect(option.value)}
                        className={`
                            relative p-4 rounded-lg border transition-all duration-200
                            text-left group cursor-pointer bg-white dark:bg-xtnd-dark-800
                            ${
                              isSelected
                                ? `${option.borderColor} shadow-md`
                                : `border-gray-200 dark:border-xtnd-dark-700 hover:border-gray-300 dark:hover:border-xtnd-dark-600 hover:shadow-sm`
                            }
                            ${errors.category ? 'border-red-500' : ''}
                          `}
                      >
                        {/* Selected Indicator */}
                        {isSelected && (
                          <div className="absolute top-3 right-3">
                            <div
                              className={`w-6 h-6 rounded-full bg-white dark:bg-xtnd-dark-800 flex items-center justify-center shadow-sm`}
                            >
                              <Icon name="HiCheck" size="sm" className={option.textColor} />
                            </div>
                          </div>
                        )}

                        {/* Icon, Title and Description Row */}
                        <div className="flex items-center gap-3">
                          {/* Icon */}
                          <div
                            className={`
                                w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0
                                ${option.bgColor}
                                transition-all duration-200
                              `}
                          >
                            <Icon
                              name={option.icon as any}
                              size="sm"
                              className={option.textColor}
                            />
                          </div>

                          {/* Title and Description */}
                          <div className="flex-1 min-w-0">
                            <h3 className="text-sm font-semibold mb-0.5 text-gray-900 dark:text-xtnd-white">
                              {option.label}
                            </h3>
                            <p className="text-xs text-gray-600 dark:text-gray-400 leading-tight line-clamp-2">
                              {option.description}
                            </p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Message */}
              <div>
                <Textarea
                  label={
                    category === 'bug'
                      ? 'Describe the bug you encountered'
                      : category === 'feature'
                        ? 'Describe the feature you would like'
                        : 'Share your feedback'
                  }
                  value={message}
                  onChange={handleMessageChange}
                  placeholder={
                    category === 'bug'
                      ? 'Please describe the issue, steps to reproduce, and expected behavior...'
                      : category === 'feature'
                        ? 'Please describe the feature, its benefits, and use cases...'
                        : 'Please describe your feedback, thoughts, or suggestions in detail...'
                  }
                  required
                  error={errors.message}
                  helperText={`${message.length}/5000 characters`}
                  fullWidth
                  autoResize
                  minRows={6}
                  maxRows={15}
                />
              </div>

              {/* User Email */}
              <div>
                <Input
                  type="email"
                  label="Your Email"
                  value={userEmail}
                  onChange={handleEmailChange}
                  placeholder="your.email@example.com"
                  required
                  error={errors.userEmail}
                  helperText="We'll use this to follow up if needed"
                  fullWidth
                  leftIcon={<Icon name="HiMail" size="sm" />}
                />
              </div>

              {/* Submit Button */}
              <div className="flex justify-end gap-3 pt-4 border-t border-xtnd-light dark:border-xtnd-dark-700">
                <Button
                  type="button"
                  variant="outline"
                  size="md"
                  onClick={() => {
                    setCategory(null);
                    setMessage('');
                    setUserEmail(currentUser?.email || '');
                    setErrors({});
                  }}
                  disabled={submitFeedback.isPending}
                >
                  Reset
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  size="md"
                  loading={submitFeedback.isPending}
                  disabled={
                    submitFeedback.isPending || !category || !message.trim() || !userEmail.trim()
                  }
                  leftIcon={<Icon name="HiPaperAirplane" size="sm" />}
                >
                  {submitFeedback.isPending ? 'Submitting...' : 'Submit Feedback'}
                </Button>
              </div>
            </form>
          </CardBody>
        </Card>
      )}
    </PageLayout>
  );
}

